import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Box, Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  BalanceCard,
  CurrencyDisplay,
  CustomTable,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  StatusChip,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { emptyText, getErrors, textCapitalization } from "utils";

const AdvancedPageFinance = () => {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const typeWeb = localStorage.getItem("typeWeb");
  const isMpis = typeWeb === "mpis";
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");
  const paramsDSP = urlParams?.get("dsp_id");
  const paramsStatus = urlParams?.get("status");

  const [loadingPage, setLoadingPage] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    dsp_id: Number(paramsDSP) || "",
    status: paramsStatus || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionDSP, setOptionDSP] = useState([]);
  const [dataSummary, setDataSummary] = useState({});

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getOptionDSP = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/master/all-client`, {
        headers,
      });
      const { data } = res?.data;
      const modifiedData = data?.map(item => ({
        ...item,
        id: item?.dsp_id,
        label: item?.dsp_name,
      }));
      setOptionDSP(modifiedData);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataTable = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/advanced`,
        {
          headers,
          params: queryParams,
        }
      );
      const { data, meta } = res?.data;
      const modifiedData = data?.map(({ period, ...item }) => {
        const [startMonth, endMonth] = period.split(" - ");
        const formattedPeriod = `${formatDatePeriod(
          startMonth
        )} - ${formatDatePeriod(endMonth)}`;
        return {
          period: formattedPeriod,
          ...item,
        };
      });
      setDataTable(modifiedData);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getDataSummary = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/summary/advanced`,
        {
          headers,
          params: {
            dsp_id: queryParams?.dsp_id,
            search: queryParams?.search,
          },
        }
      );
      setDataSummary(res?.data?.data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const debounceDataTable = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      await getDataTable();
      setLoadingPage(false);
    }, 500),
    [queryParams]
  );
  const handleDelete = async id => {
    try {
      setLoadingPage(true);
      await axios.delete(`${hardBaseUrl}/publisher/transaction/advance/${id}`, {
        headers,
      });
      ModalSuccess("Successfully delete advance").then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataSummary(), getDataTable()];

    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      setLoadingPage(false);
      isFirstLoad.current = false;
    }
  };

  useEffect(() => {
    getOptionDSP();
  }, []);
  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Advance Transaction">
      <Container maxWidth={false}>
        <HeaderTitle
          title="Advance Transaction"
          breadcrumbData={breadcrumbData}
        />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Box my="16px">
          <AutoCompleteComponent
            label={queryParams?.dsp_id === "" ? "All Client" : "Client"}
            options={optionDSP}
            value={
              optionDSP.find(
                option => option.id === Number(queryParams?.dsp_id)
              ) || null
            }
            onChange={e => handleChangeQueryParams(e, "dsp_id")}
            size="small"
          />
        </Box>
        <BalanceCard
          value={dataSummary.revenue}
          clientLabel={dataSummary?.dsp_name}
          periodLabel={dataSummary?.period}
          loading={loadingPage}
        />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <SelectInput
                  label="Status"
                  options={optionStatus}
                  optionKey="key"
                  optionLabel="value"
                  value={queryParams?.status || ""}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "status")
                  }
                  placeholder="All"
                  width={200}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Create Transaction"
                  startIcon={<Add />}
                  onClick={() => history.push("/admin/advance/add")}
                  size="large"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          isHaveAction
          handleView={item =>
            history.push(
              `/${isMpis ? "admin" : "finance"}/advance/${
                item?.publisher_transaction_id
              }`
            )
          }
          handleDelete={item => handleDelete(item?.publisher_transaction_id)}
          deleteConfirmation
          deleteConfirmationTitle="Delete Advance"
          deleteConfirmationContent={item =>
            `Are you sure you want to delete this advance (${item?.dsp_name} on period ${item?.period})?`
          }
        />
      </Container>
    </Page>
  );
};

const formatDatePeriod = date => moment(date, "MMMM YYYY").format("MMM YYYY");
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/advance",
  },
  {
    label: "Advance Transaction",
    active: true,
  },
];
const optionStatus = [
  { key: "paid", value: "Paid", type: "success" },
  { key: "on process", value: "On Process", type: "warning" },
  { key: "error", value: "Error", type: "danger" },
];
const columnTable = [
  {
    name: "dsp_name",
    title: "DSP",
  },
  {
    name: "period",
    title: "Period",
  },
  {
    name: "song_title",
    title: "Song",
    renderText: item => emptyText(item),
  },
  {
    name: "all",
    title: "Type",
    renderText: item => (
      <StatusChip label={textCapitalization(item?.type)} type="active" />
    ),
  },
  {
    name: "all",
    title: "Exchange Rate",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay
        value={item?.exchange_rate}
        decimalScale={2}
        prefix="Rp"
      />
    ),
  },
  {
    name: "all",
    title: "Base Currency Fee",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay
        value={item?.base_currency_revenue}
        decimalScale={2}
        prefix="Rp"
      />
    ),
  },
  {
    name: "all",
    title: "Origin Currency Fee",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay
        value={item?.original_currency_revenue}
        decimalScale={2}
        prefix={`${item?.currency_symbol_code}`}
      />
    ),
  },
  {
    name: "status",
    title: "Status",
    renderText: status => {
      const selectedStatus = optionStatus.find(option => option.key === status);
      return (
        <StatusChip
          label={textCapitalization(status)}
          type={selectedStatus?.type}
        />
      );
    },
  },
];
export default AdvancedPageFinance;
